/**
 * VICELESS — Main JavaScript
 * Global functionality: navigation, scroll effects, animations, newsletter, toast
 */

'use strict';

/* ================================================================
   Navigation
   ================================================================ */
function initNav() {
  const nav = document.querySelector('.nav');
  if (!nav) return;

  const hamburger = nav.querySelector('.nav__hamburger');
  const drawer = document.querySelector('.nav__drawer');

  // Scroll: add .scrolled class when past threshold
  function onScroll() {
    if (window.scrollY > 0) {
      nav.classList.add('scrolled');
    } else {
      nav.classList.remove('scrolled');
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll(); // run on load

  // Active nav link
  const currentPath = window.location.pathname.split('/').pop() || 'index.html';
  nav.querySelectorAll('.nav__link').forEach(link => {
    const href = link.getAttribute('href');
    if (href === currentPath || (currentPath === '' && href === 'index.html')) {
      link.classList.add('active');
    }
  });

  // Mobile menu toggle
  if (hamburger && drawer) {
    hamburger.addEventListener('click', () => {
      const isOpen = drawer.classList.toggle('open');
      hamburger.classList.toggle('open', isOpen);
      hamburger.setAttribute('aria-expanded', isOpen);
      document.body.style.overflow = isOpen ? 'hidden' : '';
    });

    // Close drawer on link click
    drawer.querySelectorAll('.nav__drawer-link').forEach(link => {
      link.addEventListener('click', () => {
        drawer.classList.remove('open');
        hamburger.classList.remove('open');
        hamburger.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      });
    });

    // Close on backdrop click (if drawer has backdrop)
    drawer.addEventListener('click', e => {
      if (e.target === drawer) {
        drawer.classList.remove('open');
        hamburger.classList.remove('open');
        document.body.style.overflow = '';
      }
    });
  }
}


/* ================================================================
   Scroll Reveal (Intersection Observer)
   ================================================================ */
function initReveal() {
  const elements = document.querySelectorAll('.reveal, .reveal-left');
  if (!elements.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.12,
    rootMargin: '0px 0px -40px 0px'
  });

  elements.forEach(el => observer.observe(el));
}


/* ================================================================
   Hero Image Reveal
   ================================================================ */
function initHero() {
  const hero = document.querySelector('.hero');
  if (!hero) return;

  // Add loaded class after a tick to trigger the image scale animation
  requestAnimationFrame(() => {
    setTimeout(() => hero.classList.add('loaded'), 100);
  });
}


/* ================================================================
   Newsletter Form
   ================================================================ */
function initNewsletter() {
  document.querySelectorAll('.newsletter__form').forEach(form => {
    const input = form.querySelector('.newsletter__input');
    const submit = form.querySelector('.newsletter__submit');
    const msg = form.parentElement.querySelector('.newsletter__msg');

    form.addEventListener('submit', e => {
      e.preventDefault();

      const email = input.value.trim();
      if (!email || !isValidEmail(email)) {
        if (msg) {
          msg.textContent = 'Please enter a valid email address.';
          msg.style.color = '#c00';
        }
        input.focus();
        return;
      }

      // Simulate submission
      submit.textContent = 'Joining…';
      submit.disabled = true;

      setTimeout(() => {
        if (msg) {
          msg.textContent = 'Welcome to the discipline. Check your inbox.';
          msg.style.color = 'var(--c-gray-700)';
        }
        input.value = '';
        submit.textContent = 'Subscribe';
        submit.disabled = false;
        form.style.display = 'none';
      }, 1200);
    });
  });
}


/* ================================================================
   Accordion
   ================================================================ */
function initAccordions() {
  document.querySelectorAll('.accordion-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      const body = trigger.nextElementSibling;

      // Close all others in the same section
      const section = trigger.closest('.accordion-section');
      if (section) {
        section.querySelectorAll('.accordion-trigger').forEach(t => {
          if (t !== trigger) {
            t.setAttribute('aria-expanded', 'false');
            const b = t.nextElementSibling;
            if (b) b.classList.remove('open');
          }
        });
      }

      trigger.setAttribute('aria-expanded', !expanded);
      if (body) body.classList.toggle('open', !expanded);
    });
  });
}


/* ================================================================
   Toast Notification
   ================================================================ */
function showToast(message, duration = 3500) {
  let toast = document.getElementById('toast');

  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'toast';
    toast.className = 'toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }

  toast.textContent = message;
  toast.classList.add('show');

  clearTimeout(toast._timeout);
  toast._timeout = setTimeout(() => {
    toast.classList.remove('show');
  }, duration);
}

// Make globally available
window.showToast = showToast;


/* ================================================================
   Smooth Scroll (for anchor links)
   ================================================================ */
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', e => {
      const target = document.querySelector(anchor.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
}


/* ================================================================
   Email Validation
   ================================================================ */
function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}


/* ================================================================
   Lazy Image Loading (native + fallback)
   ================================================================ */
function initLazyImages() {
  // Browsers that don't support native lazy loading
  if ('loading' in HTMLImageElement.prototype) return;

  const images = document.querySelectorAll('img[loading="lazy"]');
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        img.src = img.dataset.src || img.src;
        observer.unobserve(img);
      }
    });
  });

  images.forEach(img => observer.observe(img));
}


/* ================================================================
   Cursor effect (subtle hover highlight for product images)
   ================================================================ */
function initImageParallax() {
  // Only on desktop
  if (window.matchMedia('(max-width: 768px)').matches) return;

  document.querySelectorAll('.product-card__image-wrap').forEach(wrap => {
    wrap.addEventListener('mousemove', e => {
      const rect = wrap.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 10;
      const img = wrap.querySelector('img');
      if (img) img.style.transform = `scale(1.06) translate(${x * 0.5}px, ${y * 0.5}px)`;
    });

    wrap.addEventListener('mouseleave', () => {
      const img = wrap.querySelector('img');
      if (img) img.style.transform = '';
    });
  });
}


/* ================================================================
   Contact Form
   ================================================================ */
function initContactForm() {
  const form = document.getElementById('contact-form');
  if (!form) return;

  const successEl = document.getElementById('form-success');

  form.addEventListener('submit', e => {
    e.preventDefault();

    let valid = true;

    // Validate required fields
    form.querySelectorAll('[required]').forEach(field => {
      const group = field.closest('.form-group');
      const error = group?.querySelector('.form-error');

      if (!field.value.trim()) {
        valid = false;
        field.style.borderColor = '#c00';
        if (error) { error.textContent = 'This field is required.'; error.style.display = 'block'; }
      } else if (field.type === 'email' && !isValidEmail(field.value)) {
        valid = false;
        field.style.borderColor = '#c00';
        if (error) { error.textContent = 'Please enter a valid email.'; error.style.display = 'block'; }
      } else {
        field.style.borderColor = '';
        if (error) error.style.display = 'none';
      }
    });

    if (!valid) return;

    const submitBtn = form.querySelector('[type="submit"]');
    submitBtn.textContent = 'Sending…';
    submitBtn.disabled = true;

    // Simulate send
    setTimeout(() => {
      form.style.display = 'none';
      if (successEl) successEl.classList.add('show');
    }, 1400);
  });

  // Clear errors on input
  form.querySelectorAll('.form-input, .form-textarea, .form-select').forEach(field => {
    field.addEventListener('input', () => {
      field.style.borderColor = '';
      const group = field.closest('.form-group');
      const error = group?.querySelector('.form-error');
      if (error) error.style.display = 'none';
    });
  });
}


/* ================================================================
   Initialize Everything
   ================================================================ */
document.addEventListener('DOMContentLoaded', () => {
  initNav();
  initReveal();
  initHero();
  initNewsletter();
  initAccordions();
  initSmoothScroll();
  initLazyImages();
  initContactForm();

  // Cart init (always runs)
  Cart.init();

  // Page-specific initializers (called from inline scripts on each page)
  // initShop() — shop.js
  // initProductPage() — product.js
  // initCartPage() — cart.js
});

// Page visibility API — resume marquee animations
document.addEventListener('visibilitychange', () => {
  const marquees = document.querySelectorAll('.marquee__track');
  marquees.forEach(el => {
    el.style.animationPlayState = document.hidden ? 'paused' : 'running';
  });
});
